import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { CsvIcon } from '@/icons/csv-icon';
import { PlusIcon } from '@/icons/plus-icon';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { StaticDataType } from '@/services/staticDataType/staticDataType.types';
import * as React from 'react';
import { useDeleteStaticDataType } from '../../static-data-type/hooks/useStaticDataTypeMutations';
import { ManageStaticDataTypeContainer } from '../ManageStaticDataTypeContainer';
import { getStaticDataTypeExportKeysByColumn, keepVisibleDataTypeExportColumns } from '../utils/exportData';
import { getColumns } from './Columns';

export const StaticDataTypeTable = ({
  data,
  isLoading = false,
  pagination,
}: {
  data: StaticDataType[];
  isLoading: boolean;
  pagination: Pagination;
}) => {
  const { t } = useDynamicLocale();
  const { openConfirmation } = useConfirmationStore();
  const { onOpen } = useDialogFormStore();
  const canView = useCan({ allOf: [PERMS.static.dataLabel.list] });

  const { mutateAsync } = useDeleteStaticDataType();
  const columns = React.useMemo(
    () => getColumns(t, onOpen, openConfirmation, canView, mutateAsync),
    [t, onOpen, openConfirmation, canView, mutateAsync],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const finalData = data?.map((val: StaticDataType, index: number) => {
    return {
      SN: index + 1,
      [t('logsManagement.staticDataType.page.table.staticDataType')]: val.staticDataTypeName,
      [t('logsManagement.staticDataType.page.table.staticDataDescription')]: val.staticDataTypeDescription,
    };
  });

  const exportKeysByColumn = React.useMemo<Record<string, readonly string[]>>(
    () => getStaticDataTypeExportKeysByColumn(t),
    [t],
  );
  const canAddNew = useCan({ allOf: [PERMS.static.dataType.create] });
  const onExportCsv = () => {
    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = keepVisibleDataTypeExportColumns(finalData, visibleColumnIds, exportKeysByColumn);
    exportCsvSingleFilePapa(visibleData);
  };
  const onAddNew = () => {
    onOpen({
      title: 'logsManagement.staticDataType.manageStaticDataType.title',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'logsManagement.staticDataType.manageStaticDataType.title',
      component: <ManageStaticDataTypeContainer />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'sno', desc: true }],
    },
    meta: {
      title: t('logsManagement.staticDataType.page.title'),
      actions: [
        {
          key: 'exportCsv',
          icon: <CsvIcon />,
          onClick: onExportCsv,
        },
        ...(canAddNew
          ? [
              {
                key: 'onAddNew',
                icon: <PlusIcon />,
                onClick: onAddNew,
              },
            ]
          : []),
      ],
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.sno),
    shallow: false,
    clearOnDefault: true,
  });
  return (
    <div>
      <DataTable table={table} className="rounded-lg">
        <DataTableToolbar />
      </DataTable>
    </div>
  );
};
